import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Calendar, Target, Layers, MessageCircle, BookOpen, Zap, Image, Mic, Move, CheckCircle, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { robotoRegularBase64 } from "@/lib/robotoFont";
import { toast } from "sonner";
import jsPDF from "jspdf";

const AREA_ICONS = {
  1: Target,
  2: Layers,
  3: MessageCircle,
  4: BookOpen,
  5: Zap,
  6: Image,
  7: Mic,
  8: Move,
};

export default function Program() {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const { lang, lp, t } = useLang();

  const [assessment, setAssessment] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedWeeks, setExpandedWeeks] = useState({ 1: true });

  const user = storage.getUser();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const configData = await api.getConfig(lang);
        setConfig(configData);
        const data = await api.getAssessment(assessmentId, lang);
        setAssessment(data);
      } catch (error) {
        console.error(error);
        toast.error(t.program.errLoad);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [assessmentId, lang]);

  const toggleWeek = (weekNum) => {
    setExpandedWeeks(prev => ({ ...prev, [weekNum]: !prev[weekNum] }));
  };

  const handleBookSession = () => {
    if (config.calendar_url) {
      window.open(config.calendar_url, "_blank");
    } else {
      toast.info(t.program.calUnavailable);
    }
  };

  const handleDownloadPDF = () => {
    toast.info(t.program.generatingPDF);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const P = t.program.pdf;

      if (lang === "ru") {
        pdf.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
        pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        pdf.setFont("Roboto");
      }

      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(34, 34, 34);
      pdf.text(P.title, pageWidth / 2, 20, { align: "center" });

      // Branding
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      pdf.text(t.branding, pageWidth / 2, 28, { align: "center" });

      if (user) {
        pdf.setFontSize(12);
        pdf.setTextColor(34, 34, 34);
        pdf.text(`${P.name}: ${user.name}`, 20, 40);
        pdf.text(`${P.date}: ${new Date().toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US")}`, 20, 47);
      }

      let yPos = 60;

      assessment.program.forEach((week) => {
        if (yPos > 250) {
          pdf.addPage();
          if (lang === "ru") pdf.setFont("Roboto");
          yPos = 20;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(34, 34, 34);
        pdf.text(`${P.week} ${week.week}: ${week.primary_focus.name}`, 20, yPos);
        yPos += 8;

        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);
        const whyLines = pdf.splitTextToSize(`${P.why}: ${week.primary_focus.why_it_matters}`, 165);
        pdf.text(whyLines, 25, yPos);
        yPos += whyLines.length * 5 + 3;

        pdf.text(`${P.actions}:`, 25, yPos);
        yPos += 6;
        week.primary_focus.actions.forEach(action => {
          const actionLines = pdf.splitTextToSize(`• ${action}`, 155);
          pdf.text(actionLines, 30, yPos);
          yPos += actionLines.length * 5 + 1;
        });

        yPos += 3;
        const drillLines = pdf.splitTextToSize(`${P.drill}: ${week.primary_focus.two_minute_drill}`, 165);
        pdf.text(drillLines, 25, yPos);
        yPos += drillLines.length * 5 + 3;
        const challengeLines = pdf.splitTextToSize(`${P.challenge}: ${week.primary_focus.weekly_challenge}`, 165);
        pdf.text(challengeLines, 25, yPos);
        yPos += challengeLines.length * 5 + 10;
      });

      pdf.save(P.fileName);
      toast.success(t.program.pdfDone);
    } catch (error) {
      console.error(error);
      toast.error(t.program.pdfFail);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FFEA00] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen bg-[#222222] text-white flex items-center justify-center">
        <p>{t.program.notFound}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-5xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(lp(`/results/${assessmentId}`))}
          className="text-white/60 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.results}
        </Button>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            className="border-white/30 text-white hover:bg-white/10"
            data-testid="download-program-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {t.common.downloadPDF}
          </Button>
        </div>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.program.titlePre} <span className="text-[#FFEA00]">{t.program.titleHighlight}</span> {t.program.titlePost}
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            {t.program.subtitle}
          </p>
        </motion.div>

        {/* Week Cards */}
        <div className="space-y-6 mb-12">
          {assessment.program.map((week, idx) => {
            const PrimaryIcon = AREA_ICONS[week.primary_focus.area_id] || Target;
            const SupportIcon = week.supporting_focus ? (AREA_ICONS[week.supporting_focus.area_id] || Target) : null;
            const isExpanded = expandedWeeks[week.week];

            return (
              <motion.div
                key={week.week}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Collapsible open={isExpanded} onOpenChange={() => toggleWeek(week.week)}>
                  <Card className="bg-[#2a2a2a] border-white/5 overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <CardHeader
                        className="cursor-pointer hover:bg-white/5 transition-colors"
                        style={{ borderLeft: `4px solid ${week.primary_focus.color}` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#FFEA00] text-black font-bold text-xl flex items-center justify-center">
                              {week.week}
                            </div>
                            <div>
                              <CardTitle className="text-xl flex items-center gap-2">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${week.primary_focus.color}20` }}
                                >
                                  <PrimaryIcon className="w-4 h-4" style={{ color: week.primary_focus.color }} />
                                </div>
                                {week.primary_focus.name}
                                <span className="text-sm text-white/40 font-normal ml-2">
                                  {t.program.scoreLabel(week.primary_focus.current_score)}
                                </span>
                              </CardTitle>
                              {week.supporting_focus && (
                                <p className="text-sm text-white/50 mt-1 flex items-center gap-2">
                                  + {t.program.supporting}:
                                  <span style={{ color: week.supporting_focus.color }}>{week.supporting_focus.name}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-white/40" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white/40" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0 pb-6 px-6">
                        {/* Why It Matters */}
                        <div className="mb-6 p-4 rounded-lg bg-[#1a1a1a]">
                          <h4 className="font-semibold mb-2 text-[#FFEA00]">{t.program.whyItMatters}</h4>
                          <p className="text-white/70">{week.primary_focus.why_it_matters}</p>
                        </div>

                        {/* Actions */}
                        <div className="mb-6">
                          <h4 className="font-semibold mb-3">{t.program.whatToPractice}</h4>
                          <ul className="space-y-2">
                            {week.primary_focus.actions.map((action, i) => (
                              <li key={i} className="flex items-start gap-3 text-white/80">
                                <CheckCircle className="w-5 h-5 text-[#00E676] flex-shrink-0 mt-0.5" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                          {/* 2-Minute Drill */}
                          <div className="p-4 rounded-lg bg-[#333] border border-white/5">
                            <h4 className="font-semibold mb-2 text-sm text-white/60">{t.program.twoMinDrill}</h4>
                            <p className="text-white/80">{week.primary_focus.two_minute_drill}</p>
                          </div>

                          {/* Weekly Challenge */}
                          <div className="p-4 rounded-lg bg-[#333] border border-[#FFEA00]/20">
                            <h4 className="font-semibold mb-2 text-sm text-[#FFEA00]">{t.program.weeklyChallenge}</h4>
                            <p className="text-white/80">{week.primary_focus.weekly_challenge}</p>
                          </div>
                        </div>

                        {/* Supporting Focus */}
                        {week.supporting_focus && (
                          <div className="p-4 rounded-lg border border-white/10 mb-6">
                            <div className="flex items-center gap-2 mb-2">
                              {SupportIcon && <SupportIcon className="w-4 h-4" style={{ color: week.supporting_focus.color }} />}
                              <h4 className="font-semibold" style={{ color: week.supporting_focus.color }}>
                                {t.program.supportingFocus(week.supporting_focus.name)}
                              </h4>
                            </div>
                            <p className="text-white/70">{week.supporting_focus.mini_action}</p>
                          </div>
                        )}

                        {/* Real-Life Application */}
                        <div className="p-4 rounded-lg bg-gradient-to-r from-[#FFEA00]/10 to-transparent border border-[#FFEA00]/20">
                          <h4 className="font-semibold mb-2">{t.program.realLife}</h4>
                          <p className="text-white/80">{week.real_life_application}</p>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              </motion.div>
            );
          })}
        </div>

        {/* Booking CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-r from-[#2a2a2a] to-[#333] rounded-2xl p-8 text-center"
        >
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t.program.discussTitle}
          </h2>
          <p className="text-white/60 mb-6 max-w-lg mx-auto">
            {t.program.discussSubtitle}
          </p>
          <Button
            onClick={handleBookSession}
            className="bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold px-8 py-4 rounded-lg transition-all hover:scale-105"
            data-testid="book-session-btn"
          >
            <Calendar className="mr-2 w-5 h-5" />
            {t.program.bookSession}
            <ExternalLink className="ml-2 w-4 h-4" />
          </Button>
          <p className="text-sm text-white/40 mt-4">{t.branding}</p>
        </motion.div>
      </main>
    </div>
  );
}
